// =============================================================================
// SettingsFamily.js, /settings/family  (Phase 31S.2/3)
//
// Summit-only page where the workspace owner can:
//   • Invite a partner (spouse) by email, primary path
//   • Share a 6-character invite code as a fallback (out-of-band)
//   • See current members + pending invites
//   • Revoke pending invites or remove an active partner
//   • View the workspace audit log (full transparency per Phase 31S decision)
//
// Non-Summit users see a calm upsell card with "Upgrade to Summit" CTA.
//
// Notes:
//   • Phase 31S.1 + 31S.2 schemas must be applied for this to fully work;
//     the page degrades to an empty state with a clear message otherwise.
//   • Accountant role intentionally deferred, UI mentions it as "coming soon"
//     so users know the runway exists.
// =============================================================================
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Mail, ArrowLeft, Copy, Trash2, History, Sparkles, ShieldCheck, UserPlus,
  Crown, Heart, Briefcase, Check, AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { effectivePlan, planMeta } from '@/lib/entitlements';
import {
  listMyWorkspaces, listMembers, inviteMember, removeMember, getAuditLog,
} from '@/lib/workspaces';
import usePageTitle from '@/hooks/usePageTitle';
import { useConfirm } from '@/components/ConfirmDialog';

const ROLE_META = {
  owner:      { label: 'Owner',      icon: Crown,     tone: 'gold'    },
  partner:    { label: 'Partner',    icon: Heart,     tone: 'green'   },
  accountant: { label: 'Accountant', icon: Briefcase, tone: 'neutral' },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.partner;
  const Icon = m.icon;
  const toneClass = {
    gold:    'bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] border-[hsl(var(--gold))]/30',
    green:   'bg-primary/10 text-primary border-primary/20',
    neutral: 'bg-muted text-muted-foreground border-border',
  }[m.tone];
  return (
    <Badge variant="outline" className={`rounded-full px-2 py-0.5 text-[11px] gap-1 ${toneClass}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch (e) { return ''; }
}

export default function SettingsFamily() {
  usePageTitle('Family & Sharing');
  const { user, profile, session } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const eff = effectivePlan(profile);
  const isSummit = eff === 'summit';

  const load = useCallback(async () => {
    if (!session?.access_token) return;
    setLoading(true);
    const ws = await listMyWorkspaces(session.access_token);
    const ownedShared = (ws.workspaces || []).find(w => w.is_owner && w.kind === 'shared');
    const personal = (ws.workspaces || []).find(w => w.is_owner && w.kind === 'personal');
    const active = ownedShared || personal;
    setWorkspace(active);
    if (active) {
      const [m, a] = await Promise.all([
        listMembers(session.access_token, active.id),
        getAuditLog(session.access_token, active.id, 30),
      ]);
      setMembers(m.members || []);
      setAudit(a.entries || []);
    }
    setLoading(false);
  }, [session?.access_token]);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async () => {
    if (inviting) return;
    const email = (inviteEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!workspace?.id) {
      toast.error('No active workspace found.');
      return;
    }
    setInviting(true);
    const res = await inviteMember(session?.access_token, workspace.id, email, 'partner');
    setInviting(false);
    if (res?.ok) {
      setLastInvite(res);
      setInviteEmail('');
      toast.success('Invite created', { description: `Share the code or the email link with ${email}.` });
      load();
    } else {
      toast.error(res?.error || 'Could not send the invite.');
    }
  };

  const copyText = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      toast.error(`Could not copy, please copy manually: ${text}`);
    }
  };

  const onRemove = async (member) => {
    const isSelf = member.user_id === user?.id;
    const ok = await confirm(isSelf ? {
      title: 'Leave this workspace?',
      description: `You'll lose access to the shared data immediately. Your partner would need to re-invite you to return.`,
      confirmLabel: 'Leave workspace',
      tone: 'destructive',
    } : {
      title: `Remove ${member.display_name || member.email}?`,
      description: `They'll lose access to this workspace immediately. You can re-invite them anytime.`,
      confirmLabel: 'Remove member',
      tone: 'destructive',
    });
    if (!ok) return;
    const res = await removeMember(session?.access_token, workspace.id, member.id);
    if (res?.ok) {
      toast.success(isSelf ? 'You left the workspace' : 'Member removed');
      load();
    } else {
      toast.error(res?.error || 'Could not update membership.');
    }
  };

  // --- Upsell for non-Summit users ----------------------------------------
  if (!loading && !isSummit) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="family-upsell">
        <button onClick={() => navigate('/settings')} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to settings
        </button>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Family & Sharing</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage who can see your financial life with you.</p>

        <Card className="rounded-2xl border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/5 mt-6">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-[hsl(var(--gold))] shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-display text-xl font-semibold tracking-tight">Share clarity with your partner</h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Family & Sharing is a <span className="font-medium text-foreground">Summit</span> feature.
                  Add your spouse to the same workspace so you can plan, track, and reflect together.
                  Both of you keep your own logins; one subscription covers both.
                </p>
                <ul className="text-sm text-foreground/85 mt-3 space-y-1.5 list-disc pl-5 marker:text-[hsl(var(--gold))]">
                  <li>Co-own every transaction, account, and goal</li>
                  <li>Full audit log of who added or changed what</li>
                  <li>Flag entries for the other to review</li>
                  <li>5 concurrent sessions · Accountant role coming soon</li>
                </ul>
                <div className="mt-5 flex gap-2 flex-wrap">
                  <Button onClick={() => navigate('/pricing')} className="rounded-xl bg-[hsl(var(--gold))] text-[hsl(var(--gold-foreground))] hover:opacity-90" data-testid="family-upgrade-cta">
                    Upgrade to Summit
                  </Button>
                  <Button onClick={() => navigate('/settings')} variant="ghost" className="rounded-xl">Maybe later</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- Loading state ------------------------------------------------------
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  // --- Summit Family page -------------------------------------------------
  // Treat "no workspace fetched" (typically because Phase 31S.1 schema is
  // pending) as owner-mode for Summit users, so the invite UI is visible.
  // Real invites will only succeed once the schema is applied, but this lets
  // the UX show the right affordances right away.
  const isOwner = workspace ? workspace.is_owner : isSummit;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="family-page">
      <div>
        <button onClick={() => navigate('/settings')} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to settings
        </button>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Family & Sharing</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isOwner
            ? 'Invite your partner to co-own your 2120 workspace. One subscription, two seats.'
            : 'You’re part of a shared workspace. Below are everyone who has access.'}
        </p>
      </div>

      {/* Schema-not-applied soft notice, disappears once 31S.1 is run */}
      {!workspace && (
        <Card className="rounded-2xl border-amber-500/30 bg-amber-500/5" data-testid="family-schema-pending-notice">
          <CardContent className="p-4 sm:p-5 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <div className="text-sm font-medium text-foreground mb-1">Sharing is launching soon</div>
              We’re wiring the final database pieces for shared workspaces. The UI below is live; sending an actual invite will start working as soon as the migration is applied (we’ll email you when it ships).
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members card */}
      <Card className="rounded-2xl border-border/70" data-testid="family-members-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Members</div>
              <Badge variant="outline" className="rounded-full px-2 py-0 text-[10px]" data-testid="family-member-count">
                {members.filter(m => m.status === 'active').length} active
              </Badge>
            </div>
            {isOwner && (
              <Button onClick={() => setInviteOpen(true)} className="rounded-xl" data-testid="family-invite-btn">
                <UserPlus className="h-4 w-4 mr-1.5" /> Invite partner
              </Button>
            )}
          </div>

          <div className="mt-4 divide-y divide-border/60">
            {members.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="family-no-members">
                No members yet. Invite your partner to get started.
              </div>
            ) : (
              members.map((m) => (
                <div key={m.id} className="py-3 flex items-center gap-3" data-testid={`family-member-${m.id}`}>
                  <div className="h-9 w-9 rounded-full bg-accent grid place-items-center text-sm font-medium text-foreground/80 shrink-0">
                    {(m.display_name || m.email || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.display_name || m.email}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {m.email}{m.status === 'pending' ? ' · invite pending' : ''}
                    </div>
                  </div>
                  <RoleBadge role={m.role} />
                  {((isOwner && m.role !== 'owner') || (m.user_id === user?.id && m.role !== 'owner')) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemove(m)}
                      title={m.user_id === user?.id ? 'Leave workspace' : 'Remove'}
                      data-testid={`family-remove-${m.id}`}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Last invite reveal, show right after invite is created so the
              owner can copy/share the code immediately. */}
          {lastInvite?.ok && (
            <div className="mt-4 rounded-xl border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8 p-4" data-testid="family-last-invite">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Invite created</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-sm">Code:</span>
                <code className="px-2 py-1 rounded-md bg-background border border-border text-sm font-mono tracking-wider" data-testid="family-invite-code">
                  {lastInvite.code}
                </code>
                <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => copyText(lastInvite.code, 'Code')}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy code
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                We&rsquo;ve also sent your partner an email with this code. If it doesn&rsquo;t arrive, they can enter the code manually on the <Link to="/invite" className="text-primary hover:underline">accept invite</Link> page after signing in.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      {audit.length > 0 && (
        <Card className="rounded-2xl border-border/70" data-testid="family-audit-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-muted-foreground" />
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Activity log</div>
            </div>
            <ul className="divide-y divide-border/60">
              {audit.slice(0, 12).map((e, i) => (
                <li key={e.id || i} className="py-2.5 text-sm flex items-baseline gap-2">
                  <span className="font-medium text-foreground/85">{e.actor_display_name}</span>
                  <span className="text-muted-foreground">{e.action}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{fmtRelative(e.created_at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Trust strip */}
      <Card className="rounded-2xl border-border/70 bg-accent/40">
        <CardContent className="p-5 sm:p-6 flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground leading-relaxed">
            <div className="text-sm font-medium text-foreground mb-1">How sharing works</div>
            Both partners can add, edit, and delete the same data. Every change is attributed by name in the activity log, and either of you can flag an entry for the other to review. Accountant access (read-only with CSV export) is coming soon.
          </div>
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="family-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite your partner</DialogTitle>
            <DialogDescription>
              They’ll need an account on 2120 (free to create). Once they accept, they’ll see the same dashboard as you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-muted-foreground" htmlFor="invite-email">Partner’s email</label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="partner@example.com"
                data-testid="family-invite-email-input"
                className="mt-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              <Mail className="h-3 w-3 inline mr-1" />
              We&rsquo;ll email your partner an invitation with a 6-character code. If they don&rsquo;t receive it, you can also share the code with them manually — it&rsquo;ll appear here after you send the invite.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitInvite} disabled={inviting || !inviteEmail.trim()} className="rounded-xl" data-testid="family-invite-submit">
              {inviting ? 'Creating…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
